import { navigate, routeToHash } from '../core/router';
import { canManageRole, can, currentRole, isAdmin } from '../core/access';
import { buildTeamFilter, teamFilterMatches } from '../core/team-filter';
import { filterSelect, pickFilterSelect, wireFilterSelects } from '../core/filter-select';
import { tabIcons } from '../ui/icons';
import { esc, setPageTitle, showErr, roleLabel } from './users-shared';
import { renderTeamsTab } from './teams-tab';
import { renderTeamApiTab } from './team-api-tab';
import { openModal, closeModal } from '../ui/modal';
import { t } from '../ui/lang';
// ── Tabbed Users page ───────────────────────────────────────────────────────────
//
// The Users page is a tabbed container: "Users" (the users list/CRUD), "Teams"
// (team management, superadmin-only) and "API" (the team's integration API key +
// the docs for the endpoint it unlocks, admin and above). The two extra tabs + their
// strip buttons are gated by `can('tab.teams')` / `can('tab.teamApi')`, so a role
// that may not use them never sees them.
//
// The tabs are ANCHORED: each has its own hash (`#!/users`, `#!/users/teams`,
// `#!/users/api`) and switching one goes through the router rather than toggling
// panels in place. So a refresh, a shared link and the back button all land on the
// tab the user was actually on. Only the open tab's content is rendered.
export async function renderUsersPage(tab = 'users') {
    setPageTitle(t('users.title'));
    const content = document.getElementById('content');
    const showTeams = can('tab.teams');
    const showApi = can('tab.teamApi');
    // A deep link to a tab this role may not see falls back to "Users", and the hash is
    // corrected in place so the URL never keeps advertising a tab that isn't there.
    const active = (tab === 'teams' && !showTeams) || (tab === 'api' && !showApi) ? 'users' : tab;
    if (active !== tab) {
        history.replaceState(null, '', routeToHash({ page: 'users', tab: active }));
    }
    const tabBtn = (key, icon, label) => `<button class="settings-tab${key === active ? ' active' : ''}" data-tab="${key}">${icon}${label}</button>`;
    const tabPanel = (key) => `<div class="settings-panel settings-panel--wide" data-panel="${key}"${key === active ? '' : ' style="display:none"'}></div>`;
    content.innerHTML = `
        <div class="settings-tabs" role="tablist">
            ${tabBtn('users', tabIcons.users, t('users.tab_users'))}
            ${showTeams ? tabBtn('teams', tabIcons.teams, t('users.tab_teams')) : ''}
            ${showApi ? tabBtn('api', tabIcons.api, t('users.tab_api')) : ''}
        </div>
        ${tabPanel('users')}
        ${showTeams ? tabPanel('teams') : ''}
        ${showApi ? tabPanel('api') : ''}
    `;
    content.querySelectorAll('.settings-tab').forEach((t) => {
        t.addEventListener('click', () => navigate({ page: 'users', tab: t.dataset['tab'] }));
    });
    const body = content.querySelector(`[data-panel="${active}"]`);
    if (active === 'teams')
        await renderTeamsTab(body);
    else if (active === 'api')
        await renderTeamApiTab(body);
    else
        await renderUsersTab(body);
}
// ── "Users" tab — users list ──────────────────────────────────────────────
// Client-side filters: selected team ids and selected roles. Empty set = show all.
// Kept module-level so a re-render (e.g. after delete) preserves the selection.
const selectedTeamIds = new Set();
const selectedRoles = new Set();
// Roles offered in the role filter, high rank first (matches the badge order).
const FILTERABLE_ROLES = ['superadmin', 'admin', 'senior_operator', 'operator'];
async function renderUsersTab(panel) {
    // Superadmins manage multiple teams → multi-select team filter; admins have a
    // single team (filter pointless) and operators don't see it.
    const showTeamFilter = currentRole() === 'superadmin';
    const teamFilter = showTeamFilter ? '<span class="farm-filter__group" id="team-filter"></span>' : '';
    // Role (operator type) filter — available to admins and above.
    const showRoleFilter = isAdmin();
    const roleFilter = showRoleFilter ? '<span class="farm-filter__group" id="role-filter"></span>' : '';
    // Both facets share ONE bar, the same `.farm-filter` the farm lists use, so the
    // Users page reads like every other list instead of carrying two framed controls.
    const filterBar = roleFilter || teamFilter ? `<div class="farm-filter">${roleFilter}${teamFilter}</div>` : '';
    panel.innerHTML = `
        <div class="users-toolbar">
            <button class="add-user-btn" id="add-user-btn">${t('users.add_user')}</button>
            ${filterBar}
        </div>
        <div id="users-table-wrap"></div>
    `;
    panel.querySelector('#add-user-btn').addEventListener('click', () => navigate({ page: 'users', userForm: 'new' }));
    // Build the multi-select team filter (shared toggle-chip component). Teams come
    // from GET /api/teams; failures simply leave the filter empty (the list still loads).
    if (showTeamFilter) {
        const filterEl = panel.querySelector('#team-filter');
        if (filterEl)
            void buildTeamFilter(filterEl, selectedTeamIds, () => applyFilters(panel));
    }
    // Build the role filter — same toggle-chip look as the team filter.
    if (showRoleFilter) {
        const roleEl = panel.querySelector('#role-filter');
        if (roleEl)
            buildRoleFilter(roleEl, () => applyFilters(panel));
    }
    await loadAndRenderUsers(panel);
}
// Populate `el` with the role filter — the same drop-down component as the team filter.
// Only roles the viewer can manage (rank <= their own) are offered — a viewer never
// filters by a role above their own (mirrors canManageRole / the server rank rule).
function buildRoleFilter(el, onChange) {
    const visibleRoles = FILTERABLE_ROLES.filter((r) => canManageRole(r));
    el.innerHTML = filterSelect(t('users.role'), 'role', visibleRoles.map((r) => ({
        key: r, label: roleLabel(r), on: selectedRoles.has(r),
    })));
    if (el.dataset['wired'])
        return;
    el.dataset['wired'] = '1';
    wireFilterSelects(el);
    el.addEventListener('click', (e) => {
        const opt = e.target.closest('.fsel__opt');
        if (!opt || !el.contains(opt))
            return;
        const role = opt.dataset['key'];
        if (!role)
            return;
        if (pickFilterSelect(opt))
            selectedRoles.add(role);
        else
            selectedRoles.delete(role);
        onChange();
    });
}
// Apply the active team + role filters to the already-rendered rows (client-side, no refetch).
function applyFilters(panel) {
    const rows = panel.querySelectorAll('#users-tbody tr');
    rows.forEach((row) => {
        const raw = row.dataset['teamId'];
        const teamId = raw ? Number.parseInt(raw, 10) : null;
        const role = row.dataset['role'];
        const teamOk = teamFilterMatches(selectedTeamIds, teamId);
        const roleOk = selectedRoles.size === 0 || (role != null && selectedRoles.has(role));
        row.style.display = teamOk && roleOk ? '' : 'none';
    });
}
async function loadAndRenderUsers(panel) {
    const wrap = panel.querySelector('#users-table-wrap');
    if (!wrap)
        return;
    let users;
    try {
        const usersRes = await fetch('/api/users');
        if (!usersRes.ok) {
            wrap.innerHTML = `<p class="empty">${t('users.failed_to_load_users')}</p>`;
            return;
        }
        users = await usersRes.json();
    }
    catch {
        wrap.innerHTML = `<p class="empty">${t('users.network_error')}</p>`;
        return;
    }
    if (users.length === 0) {
        wrap.innerHTML = `<p class="empty">${t('users.no_users')}</p>`;
        return;
    }
    wrap.innerHTML = `
        <table class="users-table">
            <thead>
                <tr>
                    <th>${t('users.login')}</th>
                    <th>${t('users.telegram')}</th>
                    <th>${t('users.role')}</th>
                    <th>${t('common.team')}</th>
                    <th>${t('common.status')}</th>
                    <th>${t('users.created')}</th>
                    <th></th>
                </tr>
            </thead>
            <tbody id="users-tbody"></tbody>
        </table>
    `;
    const tbody = panel.querySelector('#users-tbody');
    for (const u of users) {
        const tr = document.createElement('tr');
        tr.dataset['id'] = String(u.id);
        tr.dataset['teamId'] = u.team_id != null ? String(u.team_id) : '';
        tr.dataset['role'] = u.role;
        // Mirror the server rank rule: hide edit/delete for targets that outrank
        // the current user (e.g. an admin viewing a superadmin row).
        const manageable = canManageRole(u.role);
        const teamName = u.team_name ?? undefined;
        const teamChip = teamName
            ? `<span class="team-chip">${esc(teamName)}</span>`
            : '<span class="u-teams-empty">—</span>';
        // Empty is worth seeing at a glance: without a handle Clicker Bot cannot tag this
        // operator, so their runs' messages stay silent in the chat.
        const tgCell = u.tg_username
            ? `@${esc(u.tg_username)}`
            : '<span class="u-teams-empty">—</span>';
        tr.innerHTML = `
            <td class="u-login">${esc(u.login)}</td>
            <td class="u-tg">${tgCell}</td>
            <td><span class="role-badge role-${u.role}">${esc(roleLabel(u.role))}</span></td>
            <td class="u-teams">${teamChip}</td>
            <td><span class="active-badge ${u.active ? 'active' : 'inactive'}">${u.active ? t('users.active') : t('users.inactive')}</span></td>
            <td class="u-date">${new Date(u.created_at).toLocaleDateString()}</td>
            <td class="u-actions">
                ${manageable ? `
                <button class="tbl-btn edit-btn" data-id="${u.id}">${t('common.edit')}</button>
                <button class="tbl-btn delete-btn" data-id="${u.id}">${t('common.delete')}</button>
                ` : ''}
            </td>
        `;
        tbody.appendChild(tr);
    }
    // Re-apply any active team/role filters to the freshly rendered rows.
    applyFilters(panel);
    tbody.addEventListener('click', (e) => {
        const target = e.target;
        const id = parseInt(target.dataset['id'] ?? '', 10);
        if (!id)
            return;
        if (target.classList.contains('edit-btn')) {
            navigate({ page: 'users', userId: id });
        }
        else if (target.classList.contains('delete-btn')) {
            showDeleteConfirm(id, users.find(x => x.id === id).login, () => void loadAndRenderUsers(panel));
        }
    });
}
// ── Delete confirm modal ──────────────────────────────────────────────────────
function showDeleteConfirm(id, login, onDone) {
    const modal = openModal(`
        <h2 class="modal__title">${t('users.delete_user_title')}</h2>
        <p class="modal__text">${t('users.delete_user_confirm', { login: `<strong>${esc(login)}</strong>` })}</p>
        <div id="uf-error" class="modal__error" style="display:none"></div>
        <div class="modal__actions">
            <button type="button" class="modal__cancel" id="uf-cancel">${t('common.cancel')}</button>
            <button type="button" class="modal__submit modal__submit--danger" id="uf-confirm">${t('common.delete')}</button>
        </div>
    `);
    modal.querySelector('#uf-cancel').addEventListener('click', closeModal);
    modal.querySelector('#uf-confirm').addEventListener('click', async () => {
        const errorEl = modal.querySelector('#uf-error');
        try {
            const res = await fetch(`/api/users/${id}`, { method: 'DELETE' });
            const data = await res.json();
            if (!res.ok) {
                showErr(errorEl, data.error ?? t('users.failed_to_delete_user'));
                return;
            }
            closeModal();
            onDone();
        }
        catch {
            showErr(errorEl, t('users.network_error'));
        }
    });
}
